import { useEffect, useReducer, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { RewardMerchantImageUpload } from '@/api/types';
import { getErrorMessage } from '@/errors/messages';
import { adminClient } from '@/lib/adminClient';
import {
  MAX_PHOTOS,
  MAX_UPLOAD_BYTES,
  isSelectableImageType,
  merchantImageIds,
  resizeImage,
  type MerchantImageKind,
  type ResizedImage,
  type ServedImage,
} from '@/lib/merchantImages';
import { putSignedUpload } from '@/lib/signedUpload';
import { rewardsKeys } from './useRewards';

// Partner photo and logo upload (LIM-4606). One file's journey is a state
// machine, `transition` below, driven by `useMerchantImageUpload`:
//
//   idle → resizing → requesting_url → uploading(progress) → attaching → attached
//
// and from any working step to failed(reason). A refusal (wrong type, no
// room) fails straight from idle. A failure on a step that is safe to repeat
// keeps what it needs to resume there: the signed URL and upload id for a
// failed PUT (a PUT to the same object is idempotent), and the upload id and
// request id for a failed registration (the request id makes it idempotent).
// Downscaling is not retried: a file that cannot be decoded will not decode
// the second time either.

/** Where the browser PUTs the bytes, and the idempotency key their registration is sent with. */
export interface SignedUpload {
  readonly uploadId: string;
  readonly uri: string;
  readonly headers: Readonly<Record<string, string>>;
  /** RFC 3339; a retry after it asks for a new URL instead. */
  readonly expireTime: string;
  readonly requestId: string;
}

export type UploadFailure =
  | 'unsupported_type'
  | 'no_room'
  | 'unreadable'
  | 'too_large'
  | 'link_failed'
  | 'upload_failed'
  | 'attach_failed';

/** The step a retry resumes at, with what that step needs. */
type Resume =
  | { readonly phase: 'requesting_url'; readonly resized: ResizedImage }
  | { readonly phase: 'uploading'; readonly resized: ResizedImage; readonly upload: SignedUpload }
  | { readonly phase: 'attaching'; readonly resized: ResizedImage; readonly upload: SignedUpload };

export type UploadState =
  | { readonly phase: 'idle' }
  | { readonly phase: 'resizing' }
  | { readonly phase: 'requesting_url'; readonly resized: ResizedImage }
  | { readonly phase: 'uploading'; readonly resized: ResizedImage; readonly upload: SignedUpload; readonly progress: number }
  | { readonly phase: 'attaching'; readonly resized: ResizedImage; readonly upload: SignedUpload }
  | { readonly phase: 'attached'; readonly image: ServedImage }
  | { readonly phase: 'failed'; readonly reason: UploadFailure; readonly message: string; readonly resume: Resume | null };

export type UploadEvent =
  | { readonly type: 'start' }
  | { readonly type: 'refuse'; readonly reason: 'unsupported_type' | 'no_room'; readonly message: string }
  | { readonly type: 'resized'; readonly resized: ResizedImage }
  | { readonly type: 'url_ready'; readonly upload: SignedUpload }
  | { readonly type: 'progress'; readonly fraction: number }
  | { readonly type: 'uploaded' }
  | { readonly type: 'attached'; readonly image: ServedImage }
  | { readonly type: 'fail'; readonly reason: UploadFailure; readonly message: string }
  /** `now` in epoch ms, passed in so the transition stays pure. */
  | { readonly type: 'retry'; readonly now: number };

function illegal(state: UploadState, event: UploadEvent): never {
  throw new Error(`an image upload cannot take "${event.type}" while ${state.phase}`);
}

/** Where a failure on a working step can resume, or null where repeating it is pointless. */
function resumeFrom(state: UploadState): Resume | null {
  switch (state.phase) {
    case 'requesting_url':
      return { phase: 'requesting_url', resized: state.resized };
    case 'uploading':
      return { phase: 'uploading', resized: state.resized, upload: state.upload };
    case 'attaching':
      return { phase: 'attaching', resized: state.resized, upload: state.upload };
    default:
      return null;
  }
}

/** The machine. Pure; an event the current step cannot take is a bug, and throws. */
export function transition(state: UploadState, event: UploadEvent): UploadState {
  switch (state.phase) {
    case 'idle':
      if (event.type === 'start') return { phase: 'resizing' };
      if (event.type === 'refuse') return { phase: 'failed', reason: event.reason, message: event.message, resume: null };
      return illegal(state, event);
    case 'resizing':
      if (event.type === 'resized') return { phase: 'requesting_url', resized: event.resized };
      break;
    case 'requesting_url':
      if (event.type === 'url_ready') return { phase: 'uploading', resized: state.resized, upload: event.upload, progress: 0 };
      break;
    case 'uploading':
      if (event.type === 'progress') return { ...state, progress: Math.min(1, Math.max(0, event.fraction)) };
      if (event.type === 'uploaded') return { phase: 'attaching', resized: state.resized, upload: state.upload };
      break;
    case 'attaching':
      if (event.type === 'attached') return { phase: 'attached', image: event.image };
      break;
    case 'attached':
      return illegal(state, event);
    case 'failed': {
      const resume = state.resume;
      if (event.type !== 'retry' || resume === null) return illegal(state, event);
      // A signed URL that has expired admits nothing: ask for a new one.
      if (resume.phase === 'uploading' && Date.parse(resume.upload.expireTime) <= event.now) {
        return { phase: 'requesting_url', resized: resume.resized };
      }
      return resume.phase === 'uploading' ? { ...resume, progress: 0 } : resume;
    }
  }
  // Every working step can fail.
  if (event.type === 'fail') return { phase: 'failed', reason: event.reason, message: event.message, resume: resumeFrom(state) };
  return illegal(state, event);
}

/** Still on its way: neither attached nor failed. */
export function isInFlight(state: UploadState): boolean {
  return state.phase !== 'attached' && state.phase !== 'failed';
}

/** A step the driver performs. */
function isWorking(state: UploadState): boolean {
  return isInFlight(state) && state.phase !== 'idle';
}

const FAILURE_COPY: Record<UploadFailure, string> = {
  unsupported_type: 'Only JPEG, PNG, WebP and SVG images can be added.',
  no_room: `A partner has at most ${MAX_PHOTOS} photos.`,
  unreadable: 'This file could not be read as an image.',
  too_large: 'Even scaled down, this image is over 5 MB. Try a smaller one.',
  link_failed: 'Could not get a link to upload it to.',
  upload_failed: 'The upload to storage failed.',
  attach_failed: 'It reached storage but could not be added to the partner.',
};

/** The upload as partner-service signed it, checked for every field the PUT and the registration need. */
function toSignedUpload(upload: RewardMerchantImageUpload, mimeType: string, requestId: string): SignedUpload {
  const { uploadId, uploadUri, uploadHeaders, expireTime } = upload;
  if (!uploadId || !uploadUri || !uploadHeaders || !expireTime) {
    throw new Error('the upload link came back without its id, URL, headers or expiry');
  }
  if (upload.mimeType !== mimeType) {
    throw new Error(`the upload link was signed for ${upload.mimeType}, not ${mimeType}`);
  }
  return { uploadId, uri: uploadUri, headers: uploadHeaders, expireTime, requestId };
}

export interface MerchantImageUploadItem {
  readonly key: string;
  readonly fileName: string;
  readonly state: UploadState;
}

type Action =
  | { readonly type: 'add'; readonly items: readonly MerchantImageUploadItem[]; readonly keep: 'unattached' | 'in_flight' }
  | { readonly type: 'event'; readonly key: string; readonly event: UploadEvent }
  | { readonly type: 'dismiss'; readonly key: string };

function uploadsReducer(items: readonly MerchantImageUploadItem[], action: Action): readonly MerchantImageUploadItem[] {
  switch (action.type) {
    case 'add': {
      const kept = items.filter((item) =>
        action.keep === 'in_flight' ? isInFlight(item.state) : item.state.phase !== 'attached',
      );
      return [...kept, ...action.items];
    }
    case 'event': {
      if (!items.some((item) => item.key === action.key)) throw new Error(`no image upload ${action.key}`);
      return items.map((item) => (item.key === action.key ? { ...item, state: transition(item.state, action.event) } : item));
    }
    case 'dismiss': {
      const item = items.find((i) => i.key === action.key);
      if (!item) throw new Error(`no image upload ${action.key}`);
      if (isInFlight(item.state)) throw new Error(`image upload ${action.key} is still ${item.state.phase}`);
      return items.filter((i) => i.key !== action.key);
    }
  }
}

export interface MerchantImageUploads {
  /** Every upload not yet attached or dismissed, in the order they were picked. */
  readonly uploads: readonly MerchantImageUploadItem[];
  /** True while any upload is still on its way; the form waits for them before saving. */
  readonly busy: boolean;
  /** Starts an upload for each file, refusing (inline, with copy) those of another type and those past `room`. */
  readonly add: (files: readonly File[], room: number) => void;
  /** Resumes a failed upload at the step that failed. */
  readonly retry: (key: string) => void;
  /** Clears a failed upload from the list. */
  readonly dismiss: (key: string) => void;
}

/**
 * Uploads photos (or a logo) of a saved partner, each through the machine
 * above. `onAttached` receives each registered image once, in the order they
 * finish; the form places it among the photos or makes it the logo, and the
 * save writes that.
 *
 * A logo slot holds one image, so adding to it clears its finished and
 * failed uploads first.
 */
export function useMerchantImageUpload(params: {
  readonly merchantId: string;
  readonly kind: MerchantImageKind;
  readonly onAttached: (image: ServedImage) => void;
}): MerchantImageUploads {
  const { merchantId, kind } = params;
  const [items, dispatch] = useReducer(uploadsReducer, []);
  const nextKey = useRef(0);
  // The latest callback, so an upload that finishes after a re-render hands
  // its image to the form as it is now.
  const onAttached = useRef(params.onAttached);
  useEffect(() => {
    onAttached.current = params.onAttached;
  });

  const failed = (reason: UploadFailure, error: unknown): UploadEvent => ({
    type: 'fail',
    reason,
    message: `${FAILURE_COPY[reason]} ${getErrorMessage(error)}`,
  });

  // Performs one working step and answers with the event it ends in. Every
  // failure becomes an event: a rejection left to escape here would surface
  // nowhere the operator looks.
  const work = async (key: string, state: UploadState, file: File | null): Promise<UploadEvent> => {
    switch (state.phase) {
      case 'resizing': {
        if (file === null) throw new Error(`image upload ${key} is downscaling without its file`);
        let resized: ResizedImage;
        try {
          resized = await resizeImage(file, kind);
        } catch (e) {
          return failed('unreadable', e);
        }
        if (resized.blob.size > MAX_UPLOAD_BYTES) {
          return { type: 'fail', reason: 'too_large', message: FAILURE_COPY.too_large };
        }
        return { type: 'resized', resized };
      }
      case 'requesting_url':
        try {
          const upload = await adminClient.createRewardMerchantImageUpload(merchantId, {
            mimeType: state.resized.mimeType,
            sizeBytes: state.resized.blob.size,
          });
          return { type: 'url_ready', upload: toSignedUpload(upload, state.resized.mimeType, crypto.randomUUID()) };
        } catch (e) {
          return failed('link_failed', e);
        }
      case 'uploading':
        try {
          await putSignedUpload({
            uri: state.upload.uri,
            headers: state.upload.headers,
            body: state.resized.blob,
            onProgress: (fraction) => dispatch({ type: 'event', key, event: { type: 'progress', fraction } }),
          });
          return { type: 'uploaded' };
        } catch (e) {
          return failed('upload_failed', e);
        }
      case 'attaching':
        try {
          const image = await adminClient.createRewardMerchantImage(merchantId, state.upload.uploadId, state.upload.requestId);
          if (!image.uri) throw new Error(`image ${image.name} came back without the URI it is served from`);
          return { type: 'attached', image: { ...image, uri: image.uri } };
        } catch (e) {
          return failed('attach_failed', e);
        }
      default:
        throw new Error(`image upload ${key} has no work while ${state.phase}`);
    }
  };

  // Runs the machine from `from` until it attaches or fails. The next state
  // is worked out here with the same pure transition the reducer applies, so
  // the driver never waits on a render to know what comes next.
  const drive = async (key: string, from: UploadState, file: File | null) => {
    let state = from;
    while (isWorking(state)) {
      const event = await work(key, state, file);
      dispatch({ type: 'event', key, event });
      state = transition(state, event);
    }
    if (state.phase === 'attached') onAttached.current(state.image);
  };

  const add = (files: readonly File[], room: number) => {
    if (merchantId === '') throw new Error('images can only be added to a saved partner');
    const added = files.map((file) => {
      nextKey.current += 1;
      const item: MerchantImageUploadItem = { key: `${kind}-${nextKey.current}`, fileName: file.name, state: { phase: 'idle' } };
      return { item, file };
    });
    dispatch({ type: 'add', items: added.map(({ item }) => item), keep: kind === 'logo' ? 'in_flight' : 'unattached' });

    let accepted = 0;
    for (const { item, file } of added) {
      let event: UploadEvent;
      if (!isSelectableImageType(file.type)) {
        event = { type: 'refuse', reason: 'unsupported_type', message: `${file.name}: ${FAILURE_COPY.unsupported_type}` };
      } else if (accepted >= room) {
        event = {
          type: 'refuse',
          reason: 'no_room',
          message: kind === 'logo' ? 'A partner has one logo: add a single image.' : FAILURE_COPY.no_room,
        };
      } else {
        accepted += 1;
        event = { type: 'start' };
      }
      dispatch({ type: 'event', key: item.key, event });
      if (event.type === 'start') void drive(item.key, transition(item.state, event), file);
    }
  };

  const retry = (key: string) => {
    const item = items.find((i) => i.key === key);
    if (!item) throw new Error(`no image upload ${key}`);
    const event: UploadEvent = { type: 'retry', now: Date.now() };
    const next = transition(item.state, event);
    dispatch({ type: 'event', key, event });
    void drive(key, next, null);
  };

  return {
    uploads: items,
    busy: items.some((item) => isInFlight(item.state)),
    add,
    retry,
    dismiss: (key) => dispatch({ type: 'dismiss', key }),
  };
}

/**
 * Deletes one of a partner's images, and its stored bytes. partner-service
 * refuses the merchant's current logo, and its copy says so.
 *
 * The merchant is marked stale rather than refetched: the form open on it
 * keeps the operator's unsaved edits, and its detail page reads it afresh the
 * next time it opens.
 */
export function useDeleteRewardMerchantImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { name: string; requestId: string }) => {
      const { merchantId, imageId } = merchantImageIds(params.name);
      return adminClient.deleteRewardMerchantImage(merchantId, imageId, params.requestId);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: rewardsKeys.merchants(), refetchType: 'none' }),
  });
}
